/*
 * /board-game-bank — the ledger: accounts, entries, replay, undo.
 *
 * Pure module: no DOM, no storage, no clock of its own (the caller passes
 * `now`). The page, the host of a room and the node tests all run the SAME
 * code, so a balance on a guest's phone is the balance the tests proved.
 *
 * The model is a journal, not a set of balances. Every operation is an entry
 * appended to `game.log`; balances are the REPLAY of the journal from the
 * starting money. That one decision buys the three things paper money and the
 * electronic banking unit do not have:
 *   · undo of ANY entry, not only the last one (a void is a flag on the entry;
 *     replay simply skips it, and a voided entry can be restored);
 *   · a money check that cannot drift: players' money + the jackpot equals
 *     what the bank issued minus what it took back, by construction — and the
 *     replay counts both sides separately, so a leak in the code would show;
 *   · a history that survives everything: export, reload, a new phone.
 *
 * Accounts: player ids ('p1'…), BANK (unlimited, issues and takes money) and
 * POT (the optional «free parking» jackpot — a real account that must never
 * go below zero).
 */

export const BANK = 'bank';
export const POT = 'pot';

export const MAX_PLAYERS = 12;
export const MAX_AMOUNT = 1e12;

/* Tokens and colours a player can pick. Twelve of each: the colours are
   distinct under the three common colour-vision deficiencies only in pairs
   with different lightness, which is why the tile also carries the token. */
export const TOKENS = ['🎩', '🚗', '🐕', '🚢', '👢', '🐈', '🦆', '🦖', '🐧', '🎸', '💎', '🚀', '🍀', '🦊', '🐻', '⭐'];
export const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#f97316', '#06b6d4', '#ec4899', '#84cc16', '#14b8a6', '#6366f1', '#a16207'];

/* Presets. The numbers of the classic economic game are the ones printed in
   its rules for decades (1 500 to start, 200 for passing Start) — the name of
   the trademark is not needed to know them. */
export const PRESETS = {
    classic: { kind: 'money', start: 1500, salary: 200, quick: [10, 20, 50, 100, 200, 500] },
    custom: { kind: 'money', start: 1000, salary: 0, quick: [10, 50, 100, 500, 1000] },
    poker: { kind: 'poker', start: 1000, salary: 0, quick: [] },
};

/* ── small helpers ────────────────────────────────────────────────────────── */

/* Built from code points on purpose: invisible characters written literally
   into source are invisible to the next person who edits the line. */
const ch = (a, b) => String.fromCharCode(a) + (b ? '-' + String.fromCharCode(b) : '');
/** Control characters, zero-width marks and bidi overrides — never in a name. */
export const INVISIBLE = new RegExp('[' + ch(0, 0x1f) + ch(0x7f) + ch(0x200b, 0x200f) + ch(0x202a, 0x202e) + ch(0x2066, 0x2069) + ch(0xfeff) + ']', 'g');
export const BOM = String.fromCharCode(0xfeff);

export function clone(o) {
    if (typeof structuredClone === 'function') return structuredClone(o);
    return JSON.parse(JSON.stringify(o));
}

export function isAccount(game, id) {
    return id === BANK || (id === POT && !!game.jackpot) || !!player(game, id);
}

export function player(game, pid) {
    for (let i = 0; i < game.players.length; i++) if (game.players[i].id === pid) return game.players[i];
    return null;
}

/** A whole, positive, finite amount — board-game money has no cents. */
export function validAmount(n) {
    return typeof n === 'number' && isFinite(n) && n > 0 && Math.floor(n) === n && n <= MAX_AMOUNT;
}

/**
 * Read an amount typed by a person: «1 500», «1,500», «1.500», «1500 ₽», «2k».
 * Returns a positive integer or null. A decimal fraction is refused rather
 * than rounded — «12.5» typed into a bank is a typo, not 12 or 13.
 */
export function parseAmount(s) {
    if (typeof s === 'number') return validAmount(s) ? s : null;
    if (typeof s !== 'string') return null;
    let t = s.trim().toLowerCase().replace(/[\s']/g, '');          // \s covers the no-break spaces of grouped numbers
    if (/[-−]/.test(t)) return null;          // a minus is a direction, and the dialog owns it
    let mul = 1;
    const m = t.match(/^(.*?)(k|к|тыс|m|м|млн)$/);
    if (m) { t = m[1]; mul = /^(k|к|тыс)$/.test(m[2]) ? 1000 : 1e6; }
    t = t.replace(/[^\d.,]/g, '');
    if (!t) return null;
    // «1,500» / «1.500» with exactly three digits after every separator is grouping
    if (/^\d{1,3}([.,]\d{3})+$/.test(t)) t = t.replace(/[.,]/g, '');
    else if (/[.,]/.test(t)) {
        const f = parseFloat(t.replace(',', '.'));
        if (!isFinite(f)) return null;
        const v = f * mul;
        return validAmount(v) ? v : (validAmount(Math.round(v)) && Math.abs(v - Math.round(v)) < 1e-9 ? Math.round(v) : null);
    }
    const v = parseInt(t, 10) * mul;
    return validAmount(v) ? v : null;
}

/* ── a new game ───────────────────────────────────────────────────────────── */

/**
 * opts: { kind, preset, start, salary, cur:{sym, before}, debt, jackpot,
 *         autoBank, players:[{name,color,token}], id, now, poker }
 */
export function newGame(opts) {
    const o = opts || {};
    const preset = PRESETS[o.preset] ? o.preset : 'classic';
    const base = PRESETS[preset];
    const kind = o.kind || base.kind;
    const start = validAmount(o.start) ? o.start : base.start;
    const salary = (typeof o.salary === 'number' && o.salary >= 0 && Math.floor(o.salary) === o.salary) ? o.salary : base.salary;
    const g = {
        v: 1,
        id: o.id || 'g' + Math.floor((o.now || 0) / 1000).toString(36),
        kind,
        preset,
        created: o.now || 0,
        updated: o.now || 0,
        rev: 0,
        cur: { sym: (o.cur && typeof o.cur.sym === 'string') ? o.cur.sym.slice(0, 4) : '', before: !!(o.cur && o.cur.before) },
        start,
        salary: kind === 'poker' ? 0 : salary,
        debt: !!o.debt,
        jackpot: kind === 'poker' ? false : !!o.jackpot,
        autoBank: !!o.autoBank,
        players: [],
        log: [],
        seq: 0,
        requests: [],
        ended: 0,
        poker: null,
    };
    const list = Array.isArray(o.players) ? o.players.slice(0, MAX_PLAYERS) : [];
    list.forEach((p, i) => {
        g.players.push({
            id: 'p' + (i + 1),
            name: cleanName(p && p.name) || String(i + 1),
            color: (p && p.color) || COLORS[i % COLORS.length],
            token: (p && p.token) || TOKENS[i % TOKENS.length],
            dev: null,
        });
    });
    g.pnext = g.players.length + 1;
    return g;
}

export function cleanName(s) {
    if (typeof s !== 'string') return '';
    // control characters and bidi overrides out: a name is shown on every phone
    return s.replace(INVISIBLE, '').trim().slice(0, 24);
}

/* ── replay ───────────────────────────────────────────────────────────────── */

/**
 * Replay the journal. Returns
 *   bal       {pid: n}      players' money (or chips)
 *   pot       n             the jackpot account
 *   issued    n             starting money handed out at the table
 *   bankOut   n             paid by the bank during the game (salary, joins…)
 *   bankIn    n             paid to the bank (taxes, purchases, cash-outs…)
 *   out       {pid: true}   bankrupt / cashed out
 *   eff       {entryId: [[from, to, amount], …]}  what each entry actually moved
 *   problems  [{id, pid, kind}]  entries that break a rule when replayed
 *   stats     {pid: {in, out}}   money received / paid, bank and players alike
 *   rebuys    {pid: n}      poker: chips bought after the start
 *   cashed    {pid: n}      poker: chips taken off the table
 *
 * `skip` — a Set of entry ids to treat as voided (what-if for undo checks),
 * `unskip` — a Set of voided ids to treat as live (what-if for restore).
 */
export function replay(game, skip, unskip) {
    const r = {
        bal: {}, pot: 0, issued: 0, bankOut: 0, bankIn: 0, out: {}, eff: {}, problems: [],
        stats: {}, rebuys: {}, cashed: {}, joined: {},
    };
    const lateIds = {};
    game.log.forEach((e) => { if (e.type === 'join') lateIds[e.pid] = true; });
    for (const p of game.players) {
        r.stats[p.id] = { in: 0, out: 0 };
        r.rebuys[p.id] = 0;
        r.cashed[p.id] = 0;
        if (lateIds[p.id]) continue;          // a late player starts at their join entry
        r.bal[p.id] = game.start;
        r.issued += game.start;
        r.joined[p.id] = true;
    }
    const neg = !game.debt;
    const problem = (e, pid, kind) => r.problems.push({ id: e.id, pid, kind });

    function move(e, from, to, amt) {
        if (!(amt > 0)) return;
        (r.eff[e.id] = r.eff[e.id] || []).push([from, to, amt]);
        if (from === BANK) r.bankOut += amt;
        else if (from === POT) { r.pot -= amt; if (r.pot < 0) problem(e, POT, 'pot'); }
        else {
            if (!r.joined[from]) problem(e, from, 'nojoin');
            if (r.out[from]) problem(e, from, 'out');
            r.bal[from] = (r.bal[from] || 0) - amt;
            r.stats[from].out += amt;
            if (neg && r.bal[from] < 0) problem(e, from, 'neg');
        }
        if (to === BANK) r.bankIn += amt;
        else if (to === POT) r.pot += amt;
        else {
            if (!r.joined[to]) problem(e, to, 'nojoin');
            if (r.out[to]) problem(e, to, 'out');
            r.bal[to] = (r.bal[to] || 0) + amt;
            r.stats[to].in += amt;
        }
    }

    for (const e of game.log) {
        const live = unskip && unskip.has(e.id) ? true : !(e.void || (skip && skip.has(e.id)));
        if (!live) continue;
        switch (e.type) {
            case 'pay':
                move(e, e.from, e.to, e.amount);
                break;
            case 'split': {
                // `others` is the list of players at the moment of the entry:
                // a player who went out later still paid their share then.
                for (const o of e.others) {
                    if (r.out[o]) continue;   // out before this entry: owes nothing, gets nothing
                    if (e.dir === 'pay') move(e, e.pid, o, e.amount);
                    else move(e, o, e.pid, e.amount);
                }
                break;
            }
            case 'bankrupt': {
                if (r.out[e.pid]) { problem(e, e.pid, 'out'); break; }
                if (e.to !== BANK && e.to !== POT && r.out[e.to]) problem(e, e.to, 'out');
                const b = r.bal[e.pid] || 0;
                if (b > 0) move(e, e.pid, e.to, b);
                else if (b < 0) move(e, BANK, e.pid, -b);     // the debt is written off by the bank
                r.out[e.pid] = true;
                break;
            }
            case 'jackpot':
                if (r.pot > 0) move(e, POT, e.pid, r.pot);
                break;
            case 'join':
                r.joined[e.pid] = true;
                r.bal[e.pid] = 0;
                move(e, BANK, e.pid, e.amount);
                break;
            case 'revive':
                if (!r.out[e.pid]) problem(e, e.pid, 'notout');
                r.out[e.pid] = false;
                break;
            case 'rebuy':
                if (r.out[e.pid]) r.out[e.pid] = false;       // coming back to the table
                move(e, BANK, e.pid, e.amount);
                r.rebuys[e.pid] += e.amount;
                break;
            case 'cashout': {
                const b = r.bal[e.pid] || 0;
                if (b > 0) move(e, e.pid, BANK, b);
                r.cashed[e.pid] += Math.max(0, b);
                r.out[e.pid] = true;
                break;
            }
            case 'hand': {
                // A finished poker hand: chips moved between players only, the
                // deltas sum to zero (checked by the poker engine and the tests).
                const moved = [];
                for (const pid of Object.keys(e.deltas)) {
                    const d = e.deltas[pid];
                    if (!d) continue;
                    r.bal[pid] = (r.bal[pid] || 0) + d;
                    if (d > 0) r.stats[pid].in += d; else r.stats[pid].out -= d;
                    moved.push([pid, d]);
                    if (r.bal[pid] < 0) problem(e, pid, 'neg');
                }
                r.eff[e.id] = moved.map(([pid, d]) => (d > 0 ? [null, pid, d] : [pid, null, -d]));
                break;
            }
            default:
                break;
        }
    }
    return r;
}

/** The money check: players' money plus the jackpot against the bank's books. */
export function moneyCheck(game, rp) {
    const r = rp || replay(game);
    let hands = 0;
    for (const pid of Object.keys(r.bal)) hands += r.bal[pid];
    const inHand = game.poker && game.poker.hand ? handStake(game.poker.hand) : 0;
    const expected = r.issued + r.bankOut - r.bankIn;
    return { hands, pot: r.pot, inHand, issued: r.issued, bankOut: r.bankOut, bankIn: r.bankIn, expected, ok: hands + r.pot === expected };
}

function handStake(h) {
    let s = 0;
    for (const pid of Object.keys(h.s || {})) s += h.s[pid].total || 0;
    return s;
}

/* ── describing entries (for the history and the export) ──────────────────── */

export function problemsKey(list) {
    return list.map((p) => p.id + ':' + p.pid + ':' + p.kind).sort();
}

/** Problems that `after` has and `before` did not. */
export function newProblems(before, after) {
    const b = new Set(problemsKey(before));
    return after.filter((p) => !b.has(p.id + ':' + p.pid + ':' + p.kind));
}

/* ── settlement (who pays whom) ───────────────────────────────────────────── */

/**
 * nets: {pid: n} summing to zero (positive = is owed). Returns the transfers
 * that settle everybody: greedy largest debtor → largest creditor, which is
 * at most n−1 transfers and exact for integer amounts.
 */
export function settle(nets) {
    const cred = [], debt = [];
    for (const pid of Object.keys(nets)) {
        const v = Math.round(nets[pid]);
        if (v > 0) cred.push([pid, v]);
        else if (v < 0) debt.push([pid, -v]);
    }
    const byAmount = (a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1);
    const out = [];
    let guard = 0;
    while (cred.length && debt.length && guard++ < 1000) {
        cred.sort(byAmount); debt.sort(byAmount);
        const c = cred[0], d = debt[0];
        const x = Math.min(c[1], d[1]);
        out.push({ from: d[0], to: c[0], amount: x });
        c[1] -= x; d[1] -= x;
        if (!c[1]) cred.shift();
        if (!d[1]) debt.shift();
    }
    return out;
}

/**
 * Nets of a poker cash game in money: (chips now + chips taken off − chips
 * bought) × price per chip. Worked in hundredths so a price of 0.5 per chip
 * stays exact, and the rounding remainder (if any) is given to the biggest
 * winner so the nets always sum to zero.
 */
export function pokerNets(game, price, rp) {
    const r = rp || replay(game);
    const perChip = price / game.start;
    const cents = {};
    let sum = 0;
    for (const p of game.players) {
        const bought = game.start + (r.rebuys[p.id] || 0);
        const now = (r.bal[p.id] || 0) + (r.cashed[p.id] || 0);
        const c = Math.round((now - bought) * perChip * 100);
        cents[p.id] = c;
        sum += c;
    }
    if (sum !== 0) {
        let top = null;
        for (const pid of Object.keys(cents)) if (top === null || cents[pid] > cents[top]) top = pid;
        if (top) cents[top] -= sum;
    }
    return cents;
}

/* ── CSV ──────────────────────────────────────────────────────────────────── */

function csvCell(v, sep) {
    const s = v == null ? '' : String(v);
    return (s.indexOf(sep) >= 0 || /["\r\n]/.test(s) || /^[=+\-@]/.test(s)) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

/**
 * rows: [[cell, …], …] → CSV text with a BOM (Excel reads UTF-8 only with it).
 * `sep` is ';' where the locale writes decimals with a comma — that is the
 * list separator Excel expects there.
 */
export function toCsv(rows, sep) {
    const s = sep || ',';
    return BOM + rows.map((r) => r.map((c) => {
        // a negative number is data, not a formula: keep it bare
        if (typeof c === 'number') return String(c);
        return csvCell(c, s);
    }).join(s)).join('\r\n') + '\r\n';
}
